import cx from 'clsx';
import type { ComponentPropsWithoutRef, ReactElement } from 'react';
import { type AvatarSize } from '../avatar/config';
import styles from './skeleton-loader.module.css';

/**
 * @deprecated Use `SkeletonLoader.Text` instead.
 */
interface HeaderProps extends ComponentPropsWithoutRef<'div'> {
  width?: string;
  lines?: number;
  smallLinesWidth?: string;
  className?: string;
}

/**
 * @deprecated Use `SkeletonLoader.Text` instead.
 */
function Header({
  width,
  lines = 0,
  smallLinesWidth = '75%',
  className,
  ...props
}: HeaderProps): ReactElement {
  return (
    <div className={cx(styles.header, className)} {...props}>
      <SkeletonLoader.Line data-test-placeholder-header-first-line standalone width={width} />
      {lines > 0 &&
        Array.from({ length: lines }, (_, index) => (
          <SkeletonLoader.Line
            data-test-placeholder-header-extra-line
            key={index}
            width={smallLinesWidth}
          />
        ))}
    </div>
  );
}

interface SkeletonLoaderWrapperProps extends ComponentPropsWithoutRef<'div'> {
  standalone?: boolean;
  width?: string;
  height?: string;
  borderRadius?: string;
  lines?: number;
  smallLinesWidth?: string;
  className?: string;
}

export type SkeletonLoaderTextVariant =
  | 'display'
  | 'page'
  | 'section'
  | 'subsection'
  | 'prominent'
  | 'standard'
  | 'caption';

/** @deprecated Use `SkeletonLoader.Text` instead of `type="line"` or `type="header"`. */
type DeprecatedSkeletonLoaderType = 'line' | 'header';

export interface SkeletonLoaderProps extends SkeletonLoaderWrapperProps {
  type?: 'avatar' | 'block' | 'button' | 'text' | DeprecatedSkeletonLoaderType;
}

export function SkeletonLoaderComponent({
  type = 'block',
  standalone = false,
  width,
  height,
  borderRadius,
  lines,
  smallLinesWidth,
  className,
  ...props
}: SkeletonLoaderProps): ReactElement {
  if (type === 'header') {
    return (
      <Header
        className={className}
        lines={lines}
        smallLinesWidth={smallLinesWidth}
        width={width}
        {...props}
      />
    );
  }

  return (
    <div
      className={cx(styles.base, styles[type], standalone && styles.standalone, className)}
      data-indeterminate-animation=""
      data-test-skeleton-loader={type}
      data-testid="skeleton-loader"
      style={{
        width,
        height,
        borderRadius,
      }}
      {...props}
    />
  );
}

/**
 * @deprecated Use `SkeletonLoader.Text` instead.
 */
function SkeletonLoaderLine({ ...props }: SkeletonLoaderWrapperProps): ReactElement {
  return <SkeletonLoaderComponent type="line" {...props} />;
}

function SkeletonLoaderBlock({ ...props }: SkeletonLoaderWrapperProps): ReactElement {
  return <SkeletonLoaderComponent type="block" {...props} />;
}

/**
 * @deprecated Use `SkeletonLoader.Text` instead.
 */
function SkeletonLoaderHeader({ ...props }: SkeletonLoaderWrapperProps): ReactElement {
  return <SkeletonLoaderComponent type="header" {...props} />;
}

interface AvatarProps extends SkeletonLoaderWrapperProps {
  size?: AvatarSize;
}

function SkeletonLoaderAvatar({ size = 'small', className, ...props }: AvatarProps): ReactElement {
  return (
    <SkeletonLoaderComponent
      type="avatar"
      className={cx(styles[`size-${size}`], className)}
      {...props}
    />
  );
}

type ButtonProps = SkeletonLoaderWrapperProps &
  ({ size: 'tertiary'; iconOnly?: never } | { size?: 'small' | 'medium'; iconOnly?: boolean });

function SkeletonLoaderButton({
  size = 'medium',
  iconOnly,
  className,
  ...props
}: ButtonProps): ReactElement {
  return (
    <SkeletonLoaderComponent
      type="button"
      className={cx(styles[`size-${size}`], iconOnly && styles['icon-only'], className)}
      {...props}
    />
  );
}

interface TextProps extends ComponentPropsWithoutRef<'div'> {
  variant?: SkeletonLoaderTextVariant;
  width?: string;
  className?: string;
}

function SkeletonLoaderText({
  variant = 'standard',
  width,
  className,
  ...props
}: TextProps): ReactElement {
  return (
    <div
      className={cx(styles.textWrapper, styles[`variant-${variant}`], className)}
      data-test-skeleton-loader="text"
      data-testid="skeleton-loader"
      style={{ width }}
      {...props}
    >
      <SkeletonLoaderComponent type="text" />
    </div>
  );
}

export const SkeletonLoader = {
  Avatar: SkeletonLoaderAvatar,
  Block: SkeletonLoaderBlock,
  Button: SkeletonLoaderButton,
  /** @deprecated Use `SkeletonLoader.Text` instead. */
  Header: SkeletonLoaderHeader,
  /** @deprecated Use `SkeletonLoader.Text` instead. */
  Line: SkeletonLoaderLine,
  Text: SkeletonLoaderText,
};
